import { useQuery } from "@tanstack/react-query";
import { apiGet, ApiError } from "../api/client";

interface PMProfile {
  username: string;
}

export function usePMSession() {
  return useQuery<PMProfile | null, ApiError>({
    queryKey: ["pm-session"],
    queryFn: async () => {
      try {
        return await apiGet<PMProfile>("/api/auth/pm/me");
      } catch (e) {
        if (e instanceof ApiError && e.status === 401) return null;
        throw e;
      }
    },
    staleTime: Infinity,
    refetchOnWindowFocus: false,
    // refetchOnMount left at default `true`. Setting `false` here previously
    // blocked the post-login refetch: the pre-login probe cached `null`, then
    // useLogin's invalidateQueries marked the query stale while no observer
    // was mounted, so on navigate(/) PMOnly remounted with the stale `null`
    // and bounced the user straight back to /login. Default `true` (refetch
    // on mount when stale) is exactly what we want here.
    //
    // Audit W4-002 — retry transport errors (TypeError "Failed to fetch",
    // brief 502 from Caddy cycling during a deploy, 503 backend restart)
    // twice with exponential backoff. ApiError shapes are structured server
    // responses (4xx/5xx with a parsed body); those are NEVER retried — a
    // 401 already returned `null` via the queryFn catch above, and any
    // other ApiError is a definitive answer. Pre-fix: `retry: false` on
    // every error, which bounced the PM to /login on any transient hiccup
    // even though their cookie session was still valid (worst-case timing:
    // a deploy-window refresh).
    retry: (failureCount, error) => {
      if (error instanceof ApiError) return false;
      return failureCount < 2;
    },
    retryDelay: (n) => Math.min(1000 * 2 ** n, 5000),
  });
}
